import React from 'react';
import './MagazineTextBlock.css';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const MagazineTextBlock = ({
  imageSrc,
  imageAlt,
  title,
  boldPara,
  paragraphs,
  paragraphs2,
  paragraphs3,
  bulletPoints,
  bulletPoints2,
  bulletType = 'bullets',
  buttonName,
  buttonLink,
  align = 'center',
  imagePosition = "beforeTitle",
  links,
  bulletsLead,
}) => {
  const { t } = useTranslation();

  // Components for <Trans>: index 0 = <strong> (existing bold via <0>…</0>),
  // index 1+ = hyperlinks via <1>…</1>, <2>…</2> when a `links` array is passed.
  // Without `links`, this is exactly [<strong />] so all other articles are unaffected.
  const transComponents = [
    <strong key="strong" />,
    ...(Array.isArray(links)
      ? links.map((href, i) => (
        <a key={`link-${i}`} href={href} target="_blank" rel="noopener noreferrer" />
      ))
      : []),
  ];

  const textAlign = align === 'left' ? 'left' : 'center';

  const normalizedParagraphs = Array.isArray(paragraphs)
    ? paragraphs
    : paragraphs
      ? [paragraphs]
      : [];

  const normalizedParagraphs2 = Array.isArray(paragraphs2) ? paragraphs2 : [];

  const normalizedBulletPoints = Array.isArray(bulletPoints) ? bulletPoints : [];

  const normalizedBulletPoints2 = Array.isArray(bulletPoints2) ? bulletPoints2 : [];

  const normalizedParagraphs3 = Array.isArray(paragraphs3) ? paragraphs3 : [];

  const renderButton = () => {
    if (!buttonName || !buttonLink) return null;

    const isInternal = buttonLink.startsWith('/');
    const isExternal = buttonLink.startsWith('http://') || buttonLink.startsWith('https://');
    const isPDF = buttonLink.toLowerCase().endsWith('.pdf');

    if (isInternal) {
      return (
        <Link to={buttonLink} className="c-magazine-text-block__button">
          {t(buttonName)}
        </Link>
      );
    }

    if (isExternal || isPDF) {
      return (
        <a
          href={buttonLink}
          className="c-magazine-text-block__button"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t(buttonName)}
        </a>
      );
    }

    return (
      <a href={buttonLink} className="c-magazine-text-block__button">
        {t(buttonName)}
      </a>
    );
  };

  return (
    <section className="c-magazine-text-block">
      <div className="c-magazine-text-block__inner" style={{ textAlign }}>
        

        <div className="c-magazine-text-block__content-wrapper">

          {imagePosition === "beforeTitle" && imageSrc && (
  <div className="c-magazine-text-block__media">
    <img
      className="c-magazine-text-block__image"
      src={imageSrc}
      alt={imageAlt ? t(imageAlt) : ""}
      loading="lazy"
    />
  </div>
)}  

          {title && (
            <h2 className="c-magazine-text-block__title">
              {t(title)}
            </h2>
          )}

          {imagePosition === "afterTitle" && imageSrc && (
  <div className="c-magazine-text-block__media">
    <img
      className="c-magazine-text-block__image"
      src={imageSrc}
      alt={imageAlt ? t(imageAlt) : ""}
      loading="lazy"
    />
  </div>
)}

          {boldPara && (
            <p className="c-magazine-text-block__lead">
              {t(boldPara)}
            </p>
          )}

          {normalizedParagraphs.length > 0 && (
            <div className="c-magazine-text-block__body">
              {normalizedParagraphs.map((p, idx) => (
                <p className="c-magazine-text-block__paragraph" key={idx}>
                  <Trans i18nKey={p} components={transComponents} />
                </p>
              ))}
            </div>
          )}

          {imagePosition === "afterParagraphs" && imageSrc && (
            <div className="c-magazine-text-block__media">
              <img
                className="c-magazine-text-block__image"
                src={imageSrc}
                alt={imageAlt ? t(imageAlt) : ""}
                loading="lazy"
              />
            </div>
          )}

          {bulletsLead && (
            <div className="c-magazine-text-block__body">
              <p className="c-magazine-text-block__paragraph">
                <Trans i18nKey={bulletsLead} components={transComponents} />
              </p>
            </div>
          )}

          {normalizedBulletPoints.length > 0 && (() => {
            const ListTag = bulletType === 'numbers' ? 'ol' : 'ul';
            const listClass = `c-magazine-text-block__list c-magazine-text-block__list--${bulletType}`;

            return (
              <ListTag className={listClass}>
                {normalizedBulletPoints.map((item, idx) => (
                  <li className="c-magazine-text-block__list-item" key={idx}>
                    {typeof item === 'string' && (
                      <Trans i18nKey={item} components={transComponents} />
                    )}
                    {typeof item === 'object' && item && (
                      <>
                        {item.point && <span className="c-magazine-text-block__list-item-point">{t(item.point)}</span>}
                        {item.paragraph && (
                          <div className="c-magazine-text-block__list-item-paragraph">
                            {t(item.paragraph)}
                          </div>
                        )}
                        {item.paragraphs && (
                          <div className="c-magazine-text-block__list-item-paragraphs">
                            {item.paragraphs.map((paragraph, idx) => (
                              <p className="c-magazine-text-block__paragraph" key={idx}>
                                <Trans i18nKey={paragraph} components={transComponents} />
                              </p>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </li>
                ))}
              </ListTag>
            );
          })()}

          {imagePosition === "afterBullets" && imageSrc && (
            <div className="c-magazine-text-block__media">
              <img
                className="c-magazine-text-block__image"
                src={imageSrc}
                alt={imageAlt ? t(imageAlt) : ""}
                loading="lazy"
              />
            </div>
          )}

          {normalizedParagraphs2.length > 0 && (
            <div className="c-magazine-text-block__body">
              {normalizedParagraphs2.map((p, idx) => (
                <p className="c-magazine-text-block__paragraph" key={idx}>
                  <Trans i18nKey={p} components={transComponents} />
                </p>
              ))}
            </div>
          )}

          {normalizedBulletPoints2.length > 0 && (
            <ul className="c-magazine-text-block__list c-magazine-text-block__list--bullets">
              {normalizedBulletPoints2.map((item, idx) => (
                <li className="c-magazine-text-text-block__list-item" key={idx}>
                  {typeof item === 'string' && (
                    <Trans i18nKey={item} components={transComponents} />
                  )}
                </li>
              ))}
            </ul>
          )}

          {normalizedParagraphs3.length > 0 && (
            <div className="c-magazine-text-block__body">
              {normalizedParagraphs3.map((p, idx) => (
                <p className="c-magazine-text-block__paragraph" key={idx}>
                  <Trans i18nKey={p} components={transComponents} />
                </p>
              ))}
            </div>
          )}

          {(buttonName && buttonLink) && (
            <div className="c-magazine-text-block__button-row">
              {renderButton()}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default MagazineTextBlock;
